/**
 * Rubina Irfan - Product Card Quick Add
 * AJAX cart functionality for product cards
 */

(function() {
  'use strict';

  function init() {
    initQuickAdd();
    initHoverImages();
  }

  // Initialize when DOM is ready
  document.addEventListener('DOMContentLoaded', init);

  // Cards appended after load - collection infinite scroll, for one - never see
  // DOMContentLoaded, so without this they carry no hover image and no
  // quick-add. Both initialisers skip anything they have already touched, so
  // re-running over the whole document is cheap and safe.
  document.addEventListener('ri:products:added', init);

  // Also init on Shopify section reloads
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', init);
  }

  // Escape hatch for any other script that injects cards.
  window.RIProductCard = window.RIProductCard || {};
  window.RIProductCard.init = init;

  /**
   * Hover images ship as data-srcset/data-src (see snippets/ri-product-card.liquid)
   * so the ~84 cards on the home page do not each request a second photo up
   * front.
   *
   * Two things this has to get right, both of which bit us:
   *
   * 1. Touch devices must never load or reveal them. :hover sticks after a tap
   *    on a phone, which used to expose an <img> that had no src yet - it
   *    painted as a broken image. The media query here matches the one guarding
   *    the reveal in ri-product-card.css, so the two cannot drift apart.
   *
   * 2. On a mouse, waiting for the pointer to arrive before starting the fetch
   *    meant the second photo appeared a beat late. Instead the fetch starts
   *    while the card is on screen, during idle time, so it is usually decoded
   *    before anyone hovers. A pointer arriving early still wins the race.
   *
   * .has-hover-image goes on the card only once the file has actually loaded;
   * ri-product-card.css keeps both the fade-out of the primary image and the
   * fade-in of the hover image behind that class, so a slow or failed request
   * simply leaves the card as it was.
   */
  function initHoverImages() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const load = (card) => {
      const img = card.querySelector('.ri-product-card__img--hover[data-srcset]');
      if (!img) return;

      img.addEventListener('load', () => card.classList.add('has-hover-image'), { once: true });

      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;

      delete img.dataset.srcset;
      delete img.dataset.src;
      delete img.dataset.sizes;
    };

    const whenIdle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));

    const observer =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries, obs) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                obs.unobserve(entry.target);
                whenIdle(() => load(entry.target));
              });
            },
            { rootMargin: '200px' }
          )
        : null;

    document.querySelectorAll('.ri-product-card').forEach((card) => {
      if (card.dataset.hoverImgInit) return;
      card.dataset.hoverImgInit = 'true';
      if (!card.querySelector('.ri-product-card__img--hover[data-srcset]')) return;

      if (observer) {
        observer.observe(card);
      } else {
        whenIdle(() => load(card));
      }

      // focusin covers keyboard users, who never generate a pointer event.
      card.addEventListener('pointerenter', () => load(card), { once: true, passive: true });
      card.addEventListener('focusin', () => load(card), { once: true, passive: true });
    });
  }

  function initQuickAdd() {
    // Find all quick-add forms
    const forms = document.querySelectorAll('.ri-quick-add-form');
    
    forms.forEach(form => {
      // Skip if already initialized
      if (form.dataset.quickAddInit) return;
      form.dataset.quickAddInit = 'true';
      
      form.addEventListener('submit', handleQuickAdd);
    });
  }

  async function handleQuickAdd(e) {
    e.preventDefault();
    
    const form = e.target;
    const button = form.querySelector('.ri-product-card__quick-btn');
    const originalText = button.textContent.trim();
    
    // Show loading state
    button.classList.add('ri--adding');
    button.textContent = 'Adding...';
    
    try {
      const formData = new FormData(form);
      const data = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity')) || 1
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      if (!response.ok) {
        throw new Error('Add to cart failed');
      }
      
      const result = await response.json();
      
      // Success state
      button.classList.remove('ri--adding');
      button.classList.add('ri--success');
      button.textContent = 'Added ✦';
      
      // Dispatch custom event for cart drawer update
      document.dispatchEvent(new CustomEvent('ri:cart:updated', {
        detail: { 
          item: result,
          total_items: result.quantity 
        }
      }));
      
      // Revert after 1.2s
      setTimeout(() => {
        button.classList.remove('ri--success');
        button.textContent = originalText;
      }, 1200);
      
    } catch (error) {
      console.error('Quick add error:', error);
      
      // Error state
      button.classList.remove('ri--adding');
      button.classList.add('ri--error');
      button.textContent = 'Unavailable';
      
      // Revert after 1.2s
      setTimeout(() => {
        button.classList.remove('ri--error');
        button.textContent = originalText;
      }, 1200);
    }
  }
})();
